import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

type SwapBudgetDistributorParams =
  components['schemas']['SwapDistributorRoleForBudgetGroupEndpoint_SwapDistributorRoleForBudgetGroupRequestBody'];
type SwapBudgetDistributorResponse =
  components['schemas']['SwapDistributorRoleForBudgetGroupEndpoint_SwapDistributorRoleForBudgetGroupResponse'];

interface SwapDistributorRoleMutationParams {
  organizationId: number;
  params: SwapBudgetDistributorParams;
}

export function useSwapDistributorRoleForBudgetGroup() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ organizationId, params }: SwapDistributorRoleMutationParams): Promise<SwapBudgetDistributorResponse> => {
      const client = createApiClient(getAccessToken);
      const { data } = await client.POST('/api/organizations/{organizationId}/budget-groups/swap-distributor', {
        params: {
          path: { organizationId },
        },
        body: {
          userId: params.userId,
          currentBudgetGroupId: params.currentBudgetGroupId,
          newBudgetGroupId: params.newBudgetGroupId,
        },
      });

      return data as SwapBudgetDistributorResponse;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['usersByBudgetGroup'] }),
        queryClient.invalidateQueries({ queryKey: ['budgetGroups'] }),
      ]);
    },
    onError: async (error: Error) => {
      throw error;
    },
  });

  return {
    swapDistributorRole: mutation.mutateAsync,
    isSwappingRole: mutation.isPending,
  };
}
